/**
 * Pool Reports Page
 *
 * Main page for viewing monthly and annual pool reports.
 * Includes pool selection, report filtering, and tabbed navigation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { ChevronsRight } from 'lucide-react';
import { clsx } from 'clsx';
import { MainLayout } from '@/layouts/MainLayout';
import { Button, Card, Alert, Skeleton, PageHeader, Container } from '@/components/ui';
import { SimpleSelect } from '@/components/ui/SimpleSelect';
import { usePoolReportsStore } from '@/store';

const REPORT_TABS = [
  { label: 'SUMMARY', value: 'summary', path: 'summary' },
  { label: 'CONTRIBUTION', value: 'contribution', path: 'contribution' },
  { label: 'NET ALLOCATION', value: 'net-allocation', path: 'net-allocation' },
  { label: 'DISTRIBUTIONS', value: 'distribution', path: 'distribution' },
  { label: 'OFFHIRE', value: 'offhire', path: 'offhire' },
  { label: 'DOWNLOAD', value: 'download', path: 'download' },
] as const;

const MOBILE_TAB_MAX = '(max-width: 767px)';

function PoolReportsScrollableTabNav({
  activeTab,
  onTabClick,
}: {
  activeTab: string;
  onTabClick: (path: string) => void;
}) {
  const navRef = useRef<HTMLElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const [scrollableMobile, setScrollableMobile] = useState(false);

  const updateScrollHints = useCallback(() => {
    const el = navRef.current;
    if (!el) return;

    const isMobile = window.matchMedia(MOBILE_TAB_MAX).matches;
    if (!isMobile) {
      setFadeLeft(false);
      setFadeRight(false);
      setScrollableMobile(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const gap = 4;
    const canScroll = scrollWidth > clientWidth + gap;
    setScrollableMobile(canScroll);
    setFadeLeft(canScroll && scrollLeft > gap);
    setFadeRight(canScroll && scrollLeft + clientWidth < scrollWidth - gap);
  }, []);

  useEffect(() => {
    updateScrollHints();
    const el = navRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollHints, { passive: true });
    const mq = window.matchMedia(MOBILE_TAB_MAX);
    const onMq = () => updateScrollHints();
    mq.addEventListener('change', onMq);

    const ro = new ResizeObserver(() => updateScrollHints());
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollHints);
      mq.removeEventListener('change', onMq);
      ro.disconnect();
    };
  }, [updateScrollHints]);

  return (
    <div>
      {scrollableMobile && (
        <p className="flex items-center gap-1.5 px-3 pb-1 text-[11px] font-medium leading-snug text-gray-500 dark:text-gray-400 sm:px-4 sm:text-xs md:hidden">
          <ChevronsRight className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
          <span>Swipe sideways for more tabs</span>
        </p>
      )}
      <div className="relative">
        <div
          className={clsx(
            'pointer-events-none absolute inset-y-0 left-0 z-10 w-7 bg-gradient-to-r from-gray-50 to-transparent transition-opacity duration-200 dark:from-dark-800 md:hidden',
            fadeLeft ? 'opacity-100' : 'opacity-0'
          )}
          aria-hidden
        />
        <div
          className={clsx(
            'pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-gray-50 to-transparent transition-opacity duration-200 dark:from-dark-800 md:hidden',
            fadeRight ? 'opacity-100' : 'opacity-0'
          )}
          aria-hidden
        />
        <nav
          ref={navRef}
          className={clsx(
            'pool-reports-tabs -mb-px flex gap-0 overflow-x-auto overscroll-x-contain scroll-smooth px-2 pb-px sm:px-3',
            'scrollbar-hide snap-x snap-mandatory md:snap-none',
            '[&>button]:snap-start'
          )}
          aria-label="Report sections"
        >
          {REPORT_TABS.map((tab) => {
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => onTabClick(tab.path)}
                className={clsx(
                  'shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors sm:px-4 sm:py-3 sm:text-sm',
                  isActive
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800 dark:text-gray-400 dark:hover:border-dark-600 dark:hover:text-gray-200'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

export function PoolReportsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeReportType = location.pathname.split('/')[2] === 'monthly' ? 'monthly' : 'yearly';

  const {
    reportType,
    reportList,
    selectedReport,
    selectedPoolId,
    pools,
    subTitle,
    loading,
    error,
    setReportType,
    setSelectedPoolId,
    fetchPools,
    fetchReportList,
    setSelectedReport,
    clearError,
  } = usePoolReportsStore();

  // Fetch pools on mount
  useEffect(() => {
    fetchPools();
  }, [fetchPools]);

  // Determine report type from URL
  useEffect(() => {
    if (routeReportType !== reportType) {
      setReportType(routeReportType);
    }
  }, [routeReportType, reportType, setReportType]);

  // Load report list when pool or report type changes
  useEffect(() => {
    if (reportType !== routeReportType) return;

    const poolIdFromQuery = searchParams.get('poolId');
    const poolId = poolIdFromQuery ? parseInt(poolIdFromQuery, 10) : selectedPoolId;

    if (poolId && !isNaN(poolId)) {
      // Clear stale fileId from URL before fetching new report list
      // This prevents child pages from making API calls with an invalid fileId
      const currentFileId = searchParams.get('fileId');
      if (currentFileId) {
        setSearchParams({ poolId: poolId.toString() }, { replace: true });
      }
      fetchReportList(poolId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, routeReportType, selectedPoolId, fetchReportList]);

  // Handle report selection from URL and auto-select first report if none selected
  useEffect(() => {
    if (reportType !== routeReportType) return;
    if (reportList.length === 0) return;

    const fileIdFromQuery = searchParams.get('fileId');
    const firstReport = reportList[0];

    if (fileIdFromQuery) {
      const report = reportList.find((r) => r.fileId === parseInt(fileIdFromQuery, 10));

      if (report) {
        // Found matching report in current pool
        if (report !== selectedReport) {
          setSelectedReport(report);
        }
      } else {
        // fileId doesn't exist in current pool's reports - default to first report
        setSelectedReport(firstReport);
        setSearchParams({
          poolId: (selectedPoolId ?? '').toString(),
          fileId: firstReport.fileId.toString(),
        });
      }
    } else {
      // No fileId in URL - auto-select first report
      setSelectedReport(firstReport);
      setSearchParams({
        poolId: (selectedPoolId ?? '').toString(),
        fileId: firstReport.fileId.toString(),
      });
    }
  }, [
    reportType,
    routeReportType,
    searchParams,
    reportList,
    selectedReport,
    selectedPoolId,
    setSelectedReport,
    setSearchParams,
  ]);

  // Navigate to first tab if on base route
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const hasTab =
      pathSegments.length > 3 && REPORT_TABS.some((tab) => tab.path === pathSegments[3]);

    if (!hasTab && reportList.length > 0) {
      navigate(`${location.pathname}/summary${location.search}`, { replace: true });
    }
  }, [location, navigate, reportList.length]);

  const handlePoolChange = (poolId: number) => {
    // Update pool ID in store and clear fileId to trigger fresh report list fetch
    setSelectedPoolId(poolId);
    // Clear selected report to force re-selection from new pool
    setSelectedReport(null);
    // Update URL with new poolId only (fileId will be set after report list loads)
    setSearchParams({ poolId: poolId.toString() });
  };

  const handleReportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fileId = e.target.value;
    const report = reportList.find((r) => r.fileId === parseInt(fileId, 10));
    if (report) {
      setSelectedReport(report);
      setSearchParams({
        poolId: (selectedPoolId ?? '').toString(),
        fileId: fileId,
      });
    }
  };

  const handleTabClick = (tabPath: string) => {
    const basePath = location.pathname.split('/').slice(0, 3).join('/');
    navigate(`${basePath}/${tabPath}${location.search}`);
  };

  const getActiveTab = () => {
    const pathSegments = location.pathname.split('/');
    const currentTab = pathSegments[3];
    return REPORT_TABS.find((tab) => tab.path === currentTab)?.value || 'summary';
  };

  const getReportOptions = () => {
    return reportList.map((report) => ({
      value: report.fileId.toString(),
      label: report.name.replace(/TI VLCC|15\+|VL Scrub/gi, '').trim(),
    }));
  };

  const reportListLabel = routeReportType === 'yearly' ? 'Annual Reports' : 'Monthly Reports';

  return (
    <MainLayout>
      <Container>
        <div className="space-y-6">
          {/* Header */}
          <PageHeader
            title={`${routeReportType === 'yearly' ? 'Annual' : 'Monthly'} Pool Reports`}
          />

          {/* Error Alert */}
          {error && (
            <Alert variant="error" onClose={clearError}>
              {error}
            </Alert>
          )}

          {/* Loading State - Skeleton UI */}
          {loading && (
            <div className="space-y-6">
              {/* Pool Selection & Tabs Skeleton */}
              <div className="overflow-hidden rounded-lg shadow-sm">
                {/* Top bar with dropdowns skeleton */}
                <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-dark-700 dark:bg-dark-800 sm:gap-3 sm:px-3 md:flex-row md:items-center">
                  <Skeleton height={40} width={180} className="rounded-md" />
                  <div className="hidden flex-1 md:block"></div>
                  <Skeleton height={40} width={180} className="rounded-md" />
                </div>

                {/* Tabs skeleton */}
                <div className="bg-gray-50 px-2 pb-0 pt-2 dark:bg-dark-800 sm:px-3 sm:pt-2.5">
                  <div className="flex gap-1.5 overflow-hidden sm:gap-2">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton
                        key={i}
                        height={40}
                        width={88}
                        className="shrink-0 rounded-t-md sm:h-11 sm:w-[5.5rem]"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Content Skeleton */}
              <Card>
                <div className="space-y-4">
                  <Skeleton height={24} width="40%" />
                  <Skeleton height={20} width="60%" />
                  <Skeleton height={20} width="50%" />
                  <div className="pt-4">
                    <Skeleton height={200} className="rounded-md" />
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Pool Selection Header - Always visible when pools are loaded */}
          {!loading && pools.length > 0 && (
            <div className="overflow-hidden rounded-lg shadow-sm">
              {/* Pool Selection & Report Dropdown - Hidden when subtitle is set */}
              {!subTitle && (
                <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-dark-700 dark:bg-dark-800 sm:gap-3 sm:px-3 md:flex-row md:items-center">
                  {/* Pool Selection Dropdown */}
                  <div className="flex w-full items-center md:w-auto">
                    <SimpleSelect
                      value={(selectedPoolId ?? '').toString()}
                      onChange={(e) => handlePoolChange(parseInt(e.target.value, 10))}
                      options={pools.map((pool) => ({
                        value: pool.PoolId.toString(),
                        label: pool.Name,
                      }))}
                      placeholder="Select Pool"
                      className="w-full md:min-w-[180px]"
                    />
                  </div>

                  {/* Spacer */}
                  <div className="hidden flex-1 md:block"></div>

                  {/* Report Selection Dropdown - Only when reports are available */}
                  {reportList.length > 0 && selectedReport && (
                    <div className="flex w-full items-center md:w-auto">
                      <SimpleSelect
                        value={selectedReport?.fileId.toString() || ''}
                        onChange={handleReportChange}
                        options={getReportOptions()}
                        placeholder={reportListLabel}
                        className="w-full md:min-w-[280px]"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Subtitle Section - Shown when subtitle is set (owner/vessel details pages) */}
              {subTitle && (
                <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-dark-700 dark:bg-dark-800 sm:px-3 sm:py-3">
                  {subTitle.backLink && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        navigate(
                          subTitle.backLink +
                            (subTitle.queryParams
                              ? '?' + new URLSearchParams(subTitle.queryParams).toString()
                              : location.search)
                        )
                      }
                      leftIcon={
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      }
                      className="rounded-full"
                      title="Back"
                    >
                      {''}
                    </Button>
                  )}
                  <h2 className="type-section-title min-w-0">{subTitle.value}</h2>
                </div>
              )}

              {/* Tabs Navigation - Only when reports are available */}
              {reportList.length > 0 && selectedReport && (
                <div className="bg-gray-50 pb-0 pt-2 dark:bg-dark-800 sm:pt-2.5 md:pt-3">
                  <PoolReportsScrollableTabNav
                    activeTab={getActiveTab()}
                    onTabClick={handleTabClick}
                  />
                </div>
              )}
            </div>
          )}

          {/* Tab Content */}
          {!loading && reportList.length > 0 && selectedReport && <Outlet />}

          {/* Empty State */}
          {!loading && reportList.length === 0 && (
            <Card>
              <div className="py-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      pools.length === 0
                        ? 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                        : 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                    }
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                  {pools.length === 0 ? 'No pools available' : 'No reports found'}
                </h3>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  {pools.length === 0
                    ? 'No pools are currently available for your account.'
                    : 'No reports are available for the selected pool.'}
                </p>
                {pools.length === 0 && (
                  <div className="mt-6">
                    <Button onClick={() => fetchPools()} size="sm">
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </Container>
    </MainLayout>
  );
}
